import React from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Button, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { CentralAmparoPageHeader } from '../../components/centralAmparo/CentralAmparoPageHeader'
import { modulesService } from '../../services/modules'
import CalendarioAgendaTab from './CalendarioAgendaTab'
import CalendarioAtendimentosTab from './CalendarioAtendimentosTab'

const MODULE_NAME = 'central_amparo_calendario'
const DEFAULT_MODULE_TITLE = 'Calendário'

type CalendarioPageTab = 'agenda' | 'atendimentos'

type CreateAppointmentAction = {
  visible: boolean
  disabled: boolean
  onClick: () => void
}

const CentralAmparoCalendario: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<CalendarioPageTab>('agenda')
  const [moduleInfo, setModuleInfo] = React.useState<{
    display_name: string
    description: string | null
  } | null>(null)
  const createClickRef = React.useRef<() => void>(() => {})
  const [showCreateAppointment, setShowCreateAppointment] = React.useState(false)
  const [createAppointmentDisabled, setCreateAppointmentDisabled] = React.useState(false)
  const handleCreateActionChange = React.useCallback((action: CreateAppointmentAction | null) => {
    if (!action?.visible) {
      setShowCreateAppointment(false)
      return
    }
    createClickRef.current = action.onClick
    setShowCreateAppointment(true)
    setCreateAppointmentDisabled(action.disabled)
  }, [])

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName(MODULE_NAME)
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || DEFAULT_MODULE_TITLE,
          description: result.data.description || null,
        })
      }
    }
    void loadModule()
  }, [])

  const tabClass = (tab: CalendarioPageTab) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      activeTab === tab
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  return (
    <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            <div className="px-6 pt-6">
              <div className="mb-6">
                <CentralAmparoPageHeader
                  title={moduleInfo?.display_name ?? DEFAULT_MODULE_TITLE}
                  description={
                    moduleInfo?.description ||
                    'Agenda e lista de atendimentos da Central de Amparo.'
                  }
                  actions={
                    showCreateAppointment ? (
                      <Button
                        onClick={() => createClickRef.current()}
                        disabled={createAppointmentDisabled}
                      >
                        <PlusIcon className="h-4 w-4" aria-hidden="true" />
                        Novo agendamento
                      </Button>
                    ) : null
                  }
                />
              </div>

              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('agenda')}
                    className={tabClass('agenda')}
                  >
                    Agenda
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('atendimentos')}
                    className={tabClass('atendimentos')}
                  >
                    Atendimentos
                  </button>
                </nav>
              </div>
            </div>

            <div className="flex-1 p-6 space-y-4">
              {/* Mantém a aba Agenda montada para preservar filtros e refresh (como no shell antigo). */}
              <div className={activeTab === 'agenda' ? 'block' : 'hidden'}>
                <CalendarioAgendaTab
                  active={activeTab === 'agenda'}
                  onCreateActionChange={handleCreateActionChange}
                />
              </div>

              {activeTab === 'atendimentos' && <CalendarioAtendimentosTab />}
            </div>
          </div>
        </PermissionGuard>
    </main>
  )
}

export default CentralAmparoCalendario
